import { Button as ButtonPrimitive } from "@base-ui/react/button"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "group/button relative inline-flex shrink-0 items-center justify-center rounded-full text-sm font-medium whitespace-nowrap transition-[background-color,color,transform,box-shadow] duration-150 outline-none select-none focus-visible:ring-4 focus-visible:ring-ring/25 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-40 aria-invalid:ring-4 aria-invalid:ring-destructive/20 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        // Confident, flat ink button (primary CTA)
        default:
          "bg-ink text-background hover:bg-ink/90",
        // Solid honey — warm, editorial accent
        honey:
          "bg-honey text-honey-foreground hover:bg-honey/90",
        // Hairline outline — quiet, default for secondary actions
        outline:
          "border border-walnut/12 bg-card text-foreground hover:bg-muted hover:border-walnut/20",
        secondary:
          "bg-muted text-foreground hover:bg-walnut/8",
        ghost:
          "text-foreground hover:bg-walnut/6",
        destructive:
          "bg-destructive/10 text-destructive hover:bg-destructive/15",
        link:
          "text-honey-deep underline-offset-4 hover:underline",
      },
      size: {
        default:
          "h-11 gap-2 px-5",
        xs: "h-7 gap-1 px-3 text-xs [&_svg:not([class*='size-'])]:size-3",
        sm: "h-9 gap-1.5 px-4 text-sm [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-12 gap-2 px-6 text-[15px]",
        xl: "h-14 gap-2.5 px-7 text-base",
        icon: "size-11",
        "icon-xs": "size-7 [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-9",
        "icon-lg": "size-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
  return (
    <ButtonPrimitive
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
